import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Validators, FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { getFailureMessageRxOp } from '@geekbears/gb-ngrx-stack-base';
import { NotificationsService } from '@geekbears/ngx-gb-ui-kit';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { isNil } from 'lodash';
import { filter, map, Observable, Subject, takeUntil, tap } from 'rxjs';

import { APP_ROUTES } from '@typings/app-routes.constant';
import { passwordValidator, getPasswordValidationMessages } from '@utils/password.validator';
import {
  forgotPasswordFailureAction,
  forgotPasswordSuccessAction,
  resetPasswordFailureAction,
  resetPasswordSuccessAction,
  UsersActions,
  forgotPasswordLoadingSelector,
  resetPasswordCodeSelector,
  resetPasswordLoadingSelector,
} from '@modules/company-manager/users/application';

@Component({
  selector: 'app-password-code',
  templateUrl: './password-code.page.html',
  styleUrls: ['./password-code.page.scss'],
})
export class PasswordCodeComponent implements OnInit, OnDestroy {
  @ViewChild('ngOtpInput') ngOtpInputRef: any;
  destroy$: Subject<void> = new Subject();
  code: string = '';
  email: string = '';
  form!: FormGroup;
  errorMessage$!: string;
  currentUser!: any;
  resetPasswordLoading$: Observable<boolean | any>;
  resendEmailLoading$: Observable<boolean | undefined>;
  constructor(
    private fb: FormBuilder,
    private router: Router,
    private notifications: NotificationsService,
    private store$: Store,
    private actions$: Actions,
    private activatedRoute: ActivatedRoute
  ) {
    this.resendEmailLoading$ = this.store$.select(
      forgotPasswordLoadingSelector
    );
    this.form = this.fb.group({
      password: ['', [Validators.required, passwordValidator()]],
      confirmPassword: ['', [Validators.required, passwordValidator()]],
      resetPasswordCode: ['', [Validators.required]],
    });
    this.resetPasswordLoading$ = this.store$.select(
      resetPasswordLoadingSelector
    );
    this.currentUser = JSON.parse(localStorage.getItem('s_user') || '{}');
  }

  getPasswordErrorMessage(controlName: string): string {
    const control = this.form?.get(controlName);
    if (!control || !control.touched || control.valid) {
      return '';
    }
    if (control.errors?.['required']) {
      return 'Please enter your password';
    }
    if (control.errors?.['passwordStrength']?.firstMessage) {
      return control.errors['passwordStrength'].firstMessage;
    }
    const msgs = getPasswordValidationMessages(control.value || '');
    return msgs.length > 0 ? msgs[0] : '';
  }

  ngOnInit(): void {
    this.onFailureActions();
    // this.getResetPasswordCode();
    this.getResetPasswordEmail();
    this.getParams();
    this.onEmailSentSuccess();
    this.onChangePasswordSuccess();
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  onEmailSentSuccess() {
    this.actions$
      .pipe(
        ofType(forgotPasswordSuccessAction),
        filter((action) => {
          return !isNil(action.response);
        }),
        map((action: any) => {
          this.notifications.success('Email sent');
          return action.response.data;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  getResetPasswordCode() {
    this.store$
      .select(resetPasswordCodeSelector)
      .pipe(
        map((response) => {
          if (response) {
            this.code = response ? response : '';
          } else {
          }
        })
      )
      .subscribe();
  }
  getResetPasswordEmail() {
    this.email = localStorage.getItem('resetPasswordEmail') || '';
  }

  checkCode() {
    if (this.form.valid) {
      if (this.code == this.form.get('code')?.value) {
        this.router.navigateByUrl(`/authentication/new-password/${this.code}`);
      } else {
        this.errorModal("The code doesn't match");
      }
    }
  }
  sendAgain() {
    this.store$.dispatch(
      UsersActions.forgotPassword({
        emailOrUsername: this.email,
      })
    );
  }

  successModal(message: string) {
    this.notifications.success(message, {
      duration: 4000,
      icon: 'check-circle',
    });
  }
  errorModal(message: string) {
    this.notifications.error(message, {
      duration: 4000,
      icon: 'alert-outline',
    });
  }
  getParams() {
    this.activatedRoute.queryParams
      .pipe(map((params) => params))
      .subscribe(async (params) => {
        if (params?.['code']) {
          const { code } = params;
          this.setCode(code);
          this.form.get('resetPasswordCode')?.setValue(code);
          this.code = code;
        }
      })
      .unsubscribe();
  }
  setCode(code: string) {
    setTimeout(() => {
      this.ngOtpInputRef.setValue(code);
    }, 1000);
  }

  onFailureActions() {
    this.actions$
      .pipe(
        ofType(forgotPasswordFailureAction, resetPasswordFailureAction),
        map((action) => {
          return action.failure;
        }),
        getFailureMessageRxOp(),
        tap((errorMessage) => {
          if (errorMessage) {
            this.notifications.error(errorMessage, {
              duration: 5000,
              icon: 'alert-outline',
            });
          }
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  changePassword() {
    if (this.form.valid) {
      if (
        this.form.get('password')?.value ==
        this.form.get('confirmPassword')?.value
      ) {
        this.store$.dispatch(
          UsersActions.resetPassword({
            resetPasswordData: this.form.value,
          })
        );
      } else {
        this.errorModal('Passwords do not match');
      }
    }
  }

  onChangePasswordSuccess() {
    this.actions$
      .pipe(
        ofType(resetPasswordSuccessAction),
        filter((action) => {
          return !isNil(action.response);
        }),
        map((action: any) => {
          this.router.navigateByUrl(
            APP_ROUTES.authentication['sign-in'].fullPath
          );
          this.notifications.success(
            'Your password have been successfully changed'
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }
  onOtpChange(event: any) {
    this.form.get('resetPasswordCode')?.setValue(event);
  }
}
